import React, { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { Flag, Send } from 'lucide-react';

/**
 * Kit Swap private chat (v39) — inside the item detail sheet, for the lister
 * and the CURRENT claimer only. Mirrors the event chat in ClubHub.jsx: the
 * same bubbles, the same 5-second poll while it's open, the same flag →
 * confirm → "Reported ✓" report flow (reports land in Club Admin →
 * Moderation). Plain text only; nothing is linkified.
 *
 * GET marks the thread read, so opening it clears the unread badge.
 */

const MAX = 500;
const POLL_MS = 5000; // same as the event chat

export default function KitChat({ clubSlug, listingId }) {
  const qc = useQueryClient();
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [reported, setReported] = useState(() => new Set());
  const listRef = useRef(null);
  const seenRef = useRef(-1);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['kit-chat', clubSlug, listingId],
    queryFn: () => axios.get(`/clubs/${clubSlug}/kit/${listingId}/messages`).then(r => r.data),
    refetchInterval: POLL_MS,
    retry: (n, err) => err?.response?.status !== 404 && n < 2,
  });
  const messages = data?.messages || [];

  // New messages arrived (and were marked read by the GET): scroll to the
  // newest and refresh the lists so the unread badges clear.
  useEffect(() => {
    if (!data) return;
    if (messages.length !== seenRef.current) {
      seenRef.current = messages.length;
      if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
      qc.invalidateQueries({ queryKey: ['kit-swap', clubSlug] });
    }
  }, [data, messages.length, qc, clubSlug]);

  if (isLoading) return null;
  if (isError || !data) return null; // not a participant / chat gone → show nothing

  const send = async () => {
    const body = text.trim();
    if (!body || sending) return;
    setSending(true); setError('');
    try {
      await axios.post(`/clubs/${clubSlug}/kit/${listingId}/messages`, { body });
      setText('');
      qc.invalidateQueries({ queryKey: ['kit-chat', clubSlug, listingId] });
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not send — try again.');
    } finally { setSending(false); }
  };

  const report = async (msg) => {
    const preview = `${msg.body.slice(0, 120)}${msg.body.length > 120 ? '…' : ''}`;
    if (!window.confirm(`Report this message from ${msg.sender_name} to your club admins?\n\n"${preview}"\n\nAn admin will be able to read this conversation.`)) return;
    try {
      await axios.post(`/clubs/${clubSlug}/kit/${listingId}/messages/${msg.id}/report`);
      setReported(prev => new Set(prev).add(msg.id));
    } catch (err) {
      alert(err?.response?.data?.error || 'Could not report the message — please try again.');
    }
  };

  const older = Math.max((data.total || 0) - messages.length, 0);

  return (
    <div className="kit-chat">
      <h3 className="kit-chat-title">Messages <span className="kit-chat-with">with {data.other_name}</span></h3>
      <div className="kit-chat-messages" ref={listRef} aria-live="polite">
        {messages.length === 0 && <div className="kit-chat-empty">{data.hint}</div>}
        {older > 0 && (
          <div className="kit-chat-empty" style={{ fontSize: 12 }}>
            {older} earlier {older === 1 ? 'message' : 'messages'} not shown.
          </div>
        )}
        {messages.map(m => (
          <div key={m.id} className={`kit-chat-msg ${m.mine ? 'mine' : ''}`}>
            <div className="kit-chat-msg-header">
              <span className="kit-chat-msg-name">{m.mine ? 'You' : m.sender_name}</span>
              <span className="kit-chat-msg-time">{format(parseISO(m.created_at), 'HH:mm · d MMM')}</span>
              {!m.mine && !m.removed && (
                reported.has(m.id) ? (
                  <span className="kit-chat-reported">Reported ✓</span>
                ) : (
                  <button type="button" className="kit-chat-flag" title="Report this message to club admins"
                    aria-label="Report message" onClick={() => report(m)}>
                    <Flag size={12} />
                  </button>
                )
              )}
            </div>
            {m.removed
              ? <div className="kit-chat-msg-body kit-chat-removed">Removed by a club admin.</div>
              : <div className="kit-chat-msg-body">{m.body}</div>}
          </div>
        ))}
      </div>

      {data.can_send ? (
        <>
          {messages.length > 0 && <p className="kit-hint kit-chat-hint">{data.hint}</p>}
          <div className="kit-chat-input">
            <input
              className="input"
              placeholder={`Message ${data.other_name}…`}
              aria-label={`Message ${data.other_name}`}
              value={text}
              maxLength={MAX}
              enterKeyHint="send"
              onChange={e => setText(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && text.trim()) { e.preventDefault(); send(); } }}
            />
            <button type="button" className="btn btn-primary btn-sm" aria-label="Send"
              disabled={!text.trim() || sending} onClick={send}>
              <Send size={14} />
            </button>
          </div>
          {text.length > MAX - 100 && <p className="kit-hint" style={{ textAlign: 'right' }}>{text.length}/{MAX}</p>}
          {error && <div className="kit-error">{error}</div>}
        </>
      ) : (
        <p className="kit-hint">
          {{
            collected: 'Collected — this chat is closed and disappears after a week.',
            left_club: 'This chat is closed — the other person is no longer a member of the club.',
            blocked: `You blocked ${data.other_name}.`, // v40 — only ever sent to the blocker
          }[data.closed_reason] || 'This chat is closed.'}
        </p>
      )}
    </div>
  );
}
